// Photo Admin API client (write endpoints).
// Read endpoints (GET /event-images, GET /event-images/activities) live in
// web/src/api/eventImages.js — kept separate to keep this admin-only file
// localized to the AdminLogin folder.

const BASE_URL =
  process.env.REACT_APP_EVENT_IMAGES_API_URL ||
  "https://7vnhfarqxg.execute-api.us-east-1.amazonaws.com/prod";

const getToken = () => sessionStorage.getItem("cognitoIdToken");

const buildAuthHeaders = () => {
  const token = getToken();
  if (!token) return { "Content-Type": "application/json" };
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const trimmedBase = () => BASE_URL.replace(/\/$/, "");

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

/**
 * Request a presigned PUT URL for direct-to-S3 upload.
 * @param {{filename:string, contentType:string, sizeBytes:number, activity:string, displayName:string}} params
 * @returns {Promise<{uploadUrl:string, s3Key:string, expiresInSec:number}>}
 */
export const requestUploadUrl = async ({
  filename,
  contentType,
  sizeBytes,
  activity,
  displayName,
}) => {
  const response = await fetch(`${trimmedBase()}/event-images/upload-url`, {
    method: "POST",
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      filename,
      contentType,
      sizeBytes,
      activity,
      displayName,
    }),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to request upload URL (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Register an uploaded image (creates the DDB row) after successful PUT to S3.
 * @param {{s3Key:string, displayName:string, activity:string, sizeBytes:number, takenAt?:string}} params
 * @returns {Promise<{item:object}>}
 */
export const createImageRecord = async ({
  s3Key,
  displayName,
  activity,
  sizeBytes,
  takenAt,
}) => {
  const body = { s3Key, displayName, activity, sizeBytes };
  if (takenAt) body.takenAt = takenAt;
  const response = await fetch(`${trimmedBase()}/event-images`, {
    method: "POST",
    headers: buildAuthHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to register image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Update an existing photo. Accepts any combination of editable fields;
 * the request body is forwarded as-is so callers control which keys are sent.
 * @param {string} s3Key - raw s3Key (will be URL-encoded internally)
 * @param {{displayName?:string, activity?:string, takenAt?:string}} body
 * @returns {Promise<{item:object}>}
 */
export const updateImageRecord = async (s3Key, body) => {
  const encoded = encodeURIComponent(s3Key);
  const response = await fetch(`${trimmedBase()}/event-images/${encoded}`, {
    method: "PATCH",
    headers: buildAuthHeaders(),
    body: JSON.stringify(body || {}),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to update image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Delete an event image by s3Key.
 * @param {string} s3Key
 * @returns {Promise<void>}
 */
export const deleteImageRecord = async (s3Key) => {
  const encoded = encodeURIComponent(s3Key);
  const response = await fetch(`${trimmedBase()}/event-images/${encoded}`, {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });

  if (!response.ok && response.status !== 204) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to delete image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }
};
